import {
  Box3,
  Euler,
  Group,
  MathUtils,
  Mesh,
  Object3D,
  Quaternion,
  Vector2,
  Vector3
} from "three";
import { CleanerModel } from "./CleanerModel";
import { addBasicObjects } from "./addBasicObjects";
import { createAmbientFishLayer } from "./AmbientFish";
import type { CleanerViewSettings, SceneProps } from "./SceneProps";
import { animate } from "@/lib/animate";
import { startWeaponUseSound, stopWeaponUseSound } from "@/lib/game-audio";
import { useSceneStore } from "@/lib/sceneStore";
import { createWeaponIkDriver } from "./createWeaponIkDriver";
import { getSceneRevealStages } from "./SceneReveal";
import { getPollutionLevelFromCounts } from "./PollutionEffects";
import {
  disposeGarbageObject,
  setRandomSafeGarbagePosition,
  showGarbageCollectionFeedback,
  setupGarbageObject,
  updateFloatingGarbageObject
} from "./GarbageObjects";
import { createGarbageHintTracker } from "./GarbageHintTracker";
import { showSuctionVortex, createPipeTravelGlow } from "./SuctionVortex";
import {
  CLEANER_HOSE_SUCTION_READY_PROGRESS,
  getCleanerHoseReleaseRestBlend,
  getCleanerHosePose,
} from "./CleanerHosePose";

const SUCTION_ANIMATION_DURATION = 1740;
const SUCTION_NEXT_ITEM_DELAY_MS = 500;
const SUCTION_ENTRY_PROGRESS = 0.7;
const SUCTION_FUNNEL_ROLL_START = 0.22;
const SUCTION_FUNNEL_MOUTH_OFFSET = 0.2;
const SUCTION_FUNNEL_ROLL_RADIUS = 0.058;
const SUCTION_FUNNEL_ROLL_TURNS = 3.2;
const SUCTION_ENTRY_SCALE = 0.32;
const SUCTION_PIPE_ENTRY_SCALE = 0.16;
const SUCTION_EXIT_SCALE = 0.09;
const SUCTION_HIT_RADIUS = 0.28;
const SUCTION_POINTER_RADIUS = 0.24;
const TAP_SUCTION_HOLD_MS = 1600;
const TAP_MOVE_CANCEL_RADIUS = 0.07;
const CLEANER_GARBAGE_MAX_CAMERA_Y_OFFSET = 0.28;
const CLEANER_HOSE_BONE_NAMES = [
  "Bone001",
  "Bone002",
  "Bone003",
  "Bone004",
  "Bone005",
  "Bone006",
  "Bone007",
  "Bone008",
  "Bone009",
  "Bone010",
  "Bone011",
  "Bone012",
  "Bone013",
  "Bone014",
  "Bone015",
  "Bone016",
  "Bone017",
  "Bone018",
  "Bone019",
  "Bone020",
  "Bone021",
  "Bone022",
  "Bone023",
  "Bone024",
  "Bone025",
  "Bone026",
  "Bone027",
  "Bone028",
  "Bone029",
  "Bone030",
  "Bone031",
  "Bone032",
  "Bone033",
  "Bone034",
  "Bone035",
  "Bone036",
  "Bone037",
  "Bone038"
];

const easeInOutCubic = (progress: number) =>
  progress < 0.5
    ? 4 * progress * progress * progress
    : 1 - Math.pow(-2 * progress + 2, 3) / 2;

const DEFAULT_CLEANER_VIEW: CleanerViewSettings = {
  /** Локальная поза пылесоса относительно инерционной HUD-группы. */
  offset: { x: 0, y: -0.24, z: -1.01 },
  /** Положительный X наклоняет верх пылесоса ближе к пользователю. */
  rotationDeg: { x: -50, y: 0, z: 0 }
};

const createCleanerDebrisTank = (cleanerRoot: Object3D) => {
  const tankGroup = new Group();
  tankGroup.name = "cleaner-debris-tank";
  tankGroup.position.set(0, -0.405, 0.035);
  tankGroup.rotation.set(0.16, 0, 0);
  cleanerRoot.add(tankGroup);

  const collectedObjects: Object3D[] = [];
  const collectedTargets: Vector3[] = [];
  const collectedBox = new Box3();
  const collectedSize = new Vector3();
  const reusableWorldPosition = new Vector3();

  const addCollectedObject = (object: Object3D, sourceScale: Vector3) => {
    object.updateMatrixWorld(true);
    tankGroup.updateMatrixWorld(true);
    object.getWorldPosition(reusableWorldPosition);

    const startLocalPosition = new Vector3().copy(reusableWorldPosition);
    tankGroup.worldToLocal(startLocalPosition);

    const fill = MathUtils.clamp(collectedObjects.length / 24, 0, 1);
    const radius = MathUtils.lerp(0.004, 0.032, Math.random());
    const angle = Math.random() * Math.PI * 2;

    const targetLocalPosition = new Vector3(
      Math.cos(angle) * radius,
      MathUtils.lerp(-0.034, 0.052, fill) + (Math.random() - 0.5) * 0.014,
      Math.sin(angle) * radius * 0.42
    );
    const startRotation = object.rotation.clone();
    const targetRotation = new Euler(
      Math.random() * Math.PI,
      Math.random() * Math.PI,
      Math.random() * Math.PI
    );
    const incomingScale = object.scale.clone();

    tankGroup.add(object);
    object.position.copy(startLocalPosition);
    object.rotation.copy(startRotation);
    object.scale.copy(sourceScale);

    object.traverse((child) => {
      child.frustumCulled = false;
      child.layers.set(1);

      if (child instanceof Mesh) {
        child.renderOrder = 1_000_004;
      }
    });

    collectedBox.setFromObject(object);
    collectedBox.getSize(collectedSize);
    const maxSize = Math.max(collectedSize.x, collectedSize.y, collectedSize.z);
    const fitScale =
      maxSize > 0.0001 ? MathUtils.clamp(0.048 / maxSize, 0.06, 1.05) : 1;

    const startScale = incomingScale;
    const targetScale = sourceScale.clone().multiplyScalar(fitScale);
    object.scale.copy(startScale);
    collectedObjects.push(object);
    collectedTargets.push(targetLocalPosition.clone());

    animate({
      duration: 420,
      onUpdate: (progress) => {
        const easedProgress = easeInOutCubic(MathUtils.clamp(progress, 0, 1));

        object.position.lerpVectors(startLocalPosition, targetLocalPosition, easedProgress);
        object.rotation.set(
          MathUtils.lerp(startRotation.x, targetRotation.x, easedProgress),
          MathUtils.lerp(startRotation.y, targetRotation.y, easedProgress),
          MathUtils.lerp(startRotation.z, targetRotation.z, easedProgress)
        );
        object.scale.lerpVectors(startScale, targetScale, easedProgress);
      },
      onComplete: () => {
        object.position.copy(targetLocalPosition);
        object.rotation.copy(targetRotation);
        object.scale.copy(targetScale);
      }
    });
  };

  return {
    addCollectedObject,
    update(time: number) {
      tankGroup.rotation.y = Math.sin(time * 0.9) * 0.18;
      tankGroup.rotation.z = Math.cos(time * 0.7) * 0.08;

      collectedObjects.forEach((piece, index) => {
        const speed = 0.9 + (index % 5) * 0.13;
        const target = collectedTargets[index];

        piece.rotation.x += 0.012 * speed;
        piece.rotation.y += 0.018 * speed;
        piece.position.x = target.x + Math.sin(time * speed + index) * 0.0016;
        piece.position.z =
          target.z + Math.cos(time * (speed + 0.2) + index) * 0.0012;
      });
    },
    dispose() {
      cleanerRoot.remove(tankGroup);
      collectedObjects.forEach(disposeGarbageObject);
      collectedObjects.length = 0;
      collectedTargets.length = 0;
    }
  };
};

const getBoneNameVariants = (name: string) => {
  const variants = [name];
  const compactMatch = /^Bone(\d+)$/.exec(name);
  const dotMatch = /^Bone\.(\d+)$/.exec(name);

  if (compactMatch) {
    variants.push(`Bone.${compactMatch[1].padStart(3, "0")}`);
  }

  if (dotMatch) {
    variants.push(`Bone${dotMatch[1].padStart(3, "0")}`);
  }

  return variants;
};

const findObjectByFlexibleName = (root: Object3D, name: string) => {
  for (const variant of getBoneNameVariants(name)) {
    const object = root.getObjectByName(variant);

    if (object) return object;
  }

  return null;
};

const cleanerBaseSwingOverrides = [
  "Bone005",
  "Bone017",
  "Bone006",
  "Bone018",
  "Bone007",
  "Bone019",
  "Bone008"
].reduce<Record<string, number>>((overrides, name) => {
  overrides[name] = MathUtils.degToRad(48);

  return overrides;
}, {});

export const startNeonsScene = (props: SceneProps) => {
  const {
    scene,
    camera,
    canvas,
    models,
    onWin,
    weaponModel,
    garbageModels
  } = props;

  const { totalGarbageCount: totalCount } = useSceneStore.getState();

  const roomSize = 5;
  const baseCameraHeight = 2;
  const garbageMaxCameraYOffset =
    props.garbageSpawnConfig?.maxCameraYOffset ?? CLEANER_GARBAGE_MAX_CAMERA_Y_OFFSET;
  const sceneStartedAt = performance.now();
  const getAmbientRevealProgress = () =>
    getSceneRevealStages(props.sceneReveal?.getProgress() ?? 1).decor;
  const getAmbientPollutionLevel = () => {
    const { collectedGarbageCount, totalGarbageCount } =
      useSceneStore.getState();
    return getPollutionLevelFromCounts(collectedGarbageCount, totalGarbageCount);
  };

  const { onExit, onUpdate, hudGroup } = addBasicObjects(props);
  const ambientFish = createAmbientFishLayer(props, {
    roomSize,
    baseCameraHeight,
  });

  const stick = models[weaponModel.key].getModel();

  if (!stick) return { onExit: () => {}, onUpdate: () => {} };

  stick.renderOrder = 1_000_000;

  stick.traverse((child) => {
    if (child instanceof Mesh) {
      child.renderOrder = 1_000_000;
    }
  });

  const applyCleanerView = () => {
    const cleanerView = props.cleanerView?.get() ?? DEFAULT_CLEANER_VIEW;

    stick.position.set(
      cleanerView.offset.x,
      cleanerView.offset.y,
      cleanerView.offset.z
    );
    stick.rotation.set(
      MathUtils.degToRad(cleanerView.rotationDeg.x),
      MathUtils.degToRad(cleanerView.rotationDeg.y),
      MathUtils.degToRad(cleanerView.rotationDeg.z)
    );
  };

  applyCleanerView();

  hudGroup.add(stick);

  const playCleanerAnim = true;

  let cleanerAnim: {
    update: (dt: number, extend: boolean) => void;
    getProgress: () => number;
    dispose: () => void;
  } | null = null;
  
  if (playCleanerAnim && weaponModel instanceof CleanerModel) {
    cleanerAnim = weaponModel.createHudAnimationDriver(stick);
  }
  const cleanerDebrisTank =
    weaponModel instanceof CleanerModel ? createCleanerDebrisTank(stick) : null;

  const weaponIk = createWeaponIkDriver({
    root: stick,
    targetSpace: hudGroup,
    chain: CLEANER_HOSE_BONE_NAMES,
    effector: CLEANER_HOSE_BONE_NAMES[CLEANER_HOSE_BONE_NAMES.length - 1],
    horizontal: { x: 0.4, y: 0, z: 0 },
    vertical: { x: 0, y: 0.4, z: 0 },
    bias: { x: 0, y: 0.02, z: 0 },
    iterations: 12,
    maxAngle: 0.22,
    minReachRatio: 0.52,
    maxReachRatio: 0.94,
    maxJointSwing: MathUtils.degToRad(35),
    // solveOrder: "base-first"
    // jointSwingOverrides: cleanerBaseSwingOverrides
  });

  let lastAnimT = performance.now();
  let lastGarbageUpdateTime = performance.now();
  const hoseBones = CLEANER_HOSE_BONE_NAMES
    .map((name) => findObjectByFlexibleName(stick, name))
    .filter((bone): bone is Object3D => bone !== null);
  const hoseRestQuaternions = hoseBones.map((bone) => bone.quaternion.clone());
  const resetHoseQuaternionsToRest = () => {
    hoseBones.forEach((bone, index) => {
      bone.quaternion.copy(hoseRestQuaternions[index]);
    });
  };
  const returnHoseQuaternionsToRest = (blend: number) => {
    hoseBones.forEach((bone, index) => {
      bone.quaternion.slerp(hoseRestQuaternions[index], blend);
    });
  };
  const hoseWaveEuler = new Euler();
  const hoseWaveQuaternion = new Quaternion();
  const hoseTargetQuaternion = new Quaternion();
  let waveAmp = 0;
  let hasCapturedExtendedHoseRestPose = false;

  const objects = new Set<Object3D>();
  const garbageHintTracker = createGarbageHintTracker(objects, camera);
  const occupiedGarbagePositions: Vector3[] = [];
  const objectWorldPosition = new Vector3();
  const projectedCoords = new Vector3();
  const projectedEffectorCoords = new Vector3();
  const projectedObject2d = new Vector2();
  const targetWorldPosition = new Vector3();
  const targetLocalPosition = new Vector3();
  const fallbackTarget = findObjectByFlexibleName(
    stick,
    CLEANER_HOSE_BONE_NAMES[CLEANER_HOSE_BONE_NAMES.length - 1]
  );

  const goods = garbageModels.map((m) => models[m.key]);

  for (let i = 0; i <= totalCount - 1; i++) {
    const model = goods[i % goods.length];
    const object = model.getModel();
    if (!object) continue;

    setRandomSafeGarbagePosition(object, roomSize, scene, camera, {
      index: i,
      count: totalCount,
      occupiedPositions: occupiedGarbagePositions,
      maxCameraYOffset: garbageMaxCameraYOffset,
      garbageKey: model.key
    });
    object.rotation.set(
      Math.random() * Math.PI * 2,
      Math.random() * Math.PI * 2,
      Math.random() * Math.PI * 2
    );
    setupGarbageObject(object, model.key);
    objects.add(object);
    occupiedGarbagePositions.push(object.position.clone());
    scene.add(object);
  }

  const mousePos = new Vector2();
  const pointerDownPos = new Vector2();
  const ikPointerTarget = new Vector2();
  const influencedIkPointerTarget = new Vector2();
  const suctionHoldPrevWorldPos = new Vector3();
  const previousTouchAction = canvas.style.touchAction;
  let activePointerId: number | null = null;
  let activeSuctionVortexCleanup: (() => void) | null = null;
  let heldVacuumFxActive = false;
  let nextSuctionAllowedAt = 0;
  let tapSuctionTarget: Object3D | null = null;
  let tapSuctionHoldUntil = 0;

  canvas.style.touchAction = "none";

  const updatePointerTarget = (e: PointerEvent) => {
    const { left, top, width, height } = canvas.getBoundingClientRect();
    const x = ((e.clientX - left) / width) * 2 - 1;
    const y = -(((e.clientY - top) / height) * 2 - 1);

    mousePos.set(
      MathUtils.clamp(x, -1, 1),
      MathUtils.clamp(y, -1, 1)
    );
  }

  const findTapSuctionTarget = () => {
    if (useSceneStore.getState().isGamePaused) return null;

    let closestObject: Object3D | null = null;
    let closestDistance = Number.POSITIVE_INFINITY;

    objects.forEach((object) => {
      if (object.userData.hasHit) return;

      object.getWorldPosition(objectWorldPosition);
      projectedCoords.copy(objectWorldPosition).project(camera);

      if (projectedCoords.z < -1 || projectedCoords.z > 1) return;

      projectedObject2d.set(projectedCoords.x, projectedCoords.y);
      const distance = mousePos.distanceTo(projectedObject2d);

      if (distance < closestDistance && distance <= SUCTION_POINTER_RADIUS) {
        closestDistance = distance;
        closestObject = object;
      }
    });

    return closestObject;
  };

  const onPointerDown = (e: PointerEvent) => {
    if (!e.isPrimary) return;

    e.preventDefault();
    updatePointerTarget(e);

    activePointerId = e.pointerId;
    canvas.setPointerCapture(e.pointerId);
    pointerDownPos.copy(mousePos);
    tapSuctionTarget = findTapSuctionTarget();
    tapSuctionHoldUntil = tapSuctionTarget
      ? performance.now() + TAP_SUCTION_HOLD_MS
      : 0;
  };

  const onPointerMove = (e: PointerEvent) => {
    if (activePointerId !== null && e.pointerId !== activePointerId) return;
    if (activePointerId === null && e.pointerType !== "mouse") return;

    e.preventDefault();
    updatePointerTarget(e);

    if (mousePos.distanceTo(pointerDownPos) > TAP_MOVE_CANCEL_RADIUS) {
      tapSuctionTarget = null;
      tapSuctionHoldUntil = 0;
    }
  };

  const onPointerEnd = (e: PointerEvent) => {
    if (activePointerId !== e.pointerId) return;

    if (canvas.hasPointerCapture(e.pointerId)) {
      canvas.releasePointerCapture(e.pointerId);
    }

    activePointerId = null;
    if (!tapSuctionTarget) {
      updateHeldVacuumFx(false);
    }
  };

  const getHoldSuctionTipTransform = (pos: Vector3, dir: Vector3) => {
    const tipBone = hoseBones[hoseBones.length - 1];

    if (tipBone) {
      tipBone.getWorldPosition(pos);
      const prevBone = hoseBones[hoseBones.length - 2];
      if (prevBone) {
        prevBone.getWorldPosition(suctionHoldPrevWorldPos);
        dir.subVectors(pos, suctionHoldPrevWorldPos).normalize();
      } else {
        dir.set(0, -1, 0);
      }
      return;
    }

    if (weaponIk.isReady) {
      weaponIk.getEffectorWorldPosition(pos);
    } else {
      fallbackTarget?.getWorldPosition(pos);
    }
    dir.set(0, -1, 0);
  };

  const updateHeldVacuumFx = (active: boolean) => {
    if (heldVacuumFxActive === active) return;
    heldVacuumFxActive = active;

    if (!active) {
      activeSuctionVortexCleanup?.();
      activeSuctionVortexCleanup = null;
      return;
    }

    if (activeSuctionVortexCleanup) return;

    activeSuctionVortexCleanup = showSuctionVortex(
      scene,
      getHoldSuctionTipTransform,
      1,
      {
        forwardOffset: 0.16,
        renderOrder: 1_000_001,
        scale: 1.12,
        sustain: true,
        swirlStrength: 0.88,
      },
    );
  };

  canvas.addEventListener('pointerdown', onPointerDown);
  canvas.addEventListener('pointermove', onPointerMove);
  canvas.addEventListener('pointerup', onPointerEnd);
  canvas.addEventListener('pointercancel', onPointerEnd);
  canvas.addEventListener('lostpointercapture', onPointerEnd);

  return {
    onExit: () => {
      cleanerAnim?.dispose();
      cleanerDebrisTank?.dispose();
      ambientFish.dispose();
      objects.forEach(disposeGarbageObject);
      objects.clear();
      updateHeldVacuumFx(false);
      stopWeaponUseSound();
      canvas.style.touchAction = previousTouchAction;
      canvas.removeEventListener('pointerdown', onPointerDown);
      canvas.removeEventListener('pointermove', onPointerMove);
      canvas.removeEventListener('pointerup', onPointerEnd);
      canvas.removeEventListener('pointercancel', onPointerEnd);
      canvas.removeEventListener('lostpointercapture', onPointerEnd);
      garbageHintTracker.dispose();
      onExit();
    },
    onUpdate: () => {
      onUpdate();
      const frameNow = performance.now();
      const garbageDeltaSeconds = Math.min(
        (frameNow - lastGarbageUpdateTime) / 1000,
        0.05
      );
      lastGarbageUpdateTime = frameNow;
      const swayT = (frameNow - sceneStartedAt) / 1000;
      ambientFish.update(swayT, getAmbientRevealProgress(), getAmbientPollutionLevel());
      applyCleanerView();

      const now = performance.now();
      const animDeltaSeconds = Math.min((now - lastAnimT) / 1000, 0.05);
      const isGamePaused = useSceneStore.getState().isGamePaused;
      if (
        isGamePaused ||
        frameNow > tapSuctionHoldUntil ||
        (tapSuctionTarget &&
          (!objects.has(tapSuctionTarget) || tapSuctionTarget.userData.hasHit))
      ) {
        tapSuctionTarget = null;
        tapSuctionHoldUntil = 0;
      }

      if (tapSuctionTarget && activePointerId === null) {
        tapSuctionTarget.getWorldPosition(objectWorldPosition);
        projectedCoords.copy(objectWorldPosition).project(camera);
        mousePos.set(projectedCoords.x, projectedCoords.y);
      }

      const isPointing = activePointerId !== null || tapSuctionTarget !== null;
      updateHeldVacuumFx(isPointing && !isGamePaused);

      if (cleanerAnim) {
        cleanerAnim.update(animDeltaSeconds, isPointing);
      }
      lastAnimT = now;
      cleanerDebrisTank?.update(swayT);

      const hoseProgress = cleanerAnim?.getProgress() ?? 1;
      const hosePose = getCleanerHosePose(hoseProgress);
      const canControlHose = isPointing && hosePose.ikInfluence > 0;
      const canSuck =
        isPointing &&
        hoseProgress >= CLEANER_HOSE_SUCTION_READY_PROGRESS &&
        frameNow >= nextSuctionAllowedAt;

      if (!isPointing) {
        hasCapturedExtendedHoseRestPose = false;
        ikPointerTarget.set(0, 0);
      }

      waveAmp += ((isPointing ? 0 : 1) - waveAmp) * 0.025;

      if (canControlHose) {
        resetHoseQuaternionsToRest();
        weaponIk.resetRestPose();

        influencedIkPointerTarget
          .copy(mousePos)
          .multiplyScalar(hosePose.ikInfluence);
        if (!hasCapturedExtendedHoseRestPose) {
          ikPointerTarget.copy(influencedIkPointerTarget);
          hasCapturedExtendedHoseRestPose = true;
        }

        ikPointerTarget.lerp(
          influencedIkPointerTarget,
          MathUtils.lerp(0.12, 0.28, hosePose.ikInfluence)
        );
        weaponIk.setPointerTarget(ikPointerTarget);
        weaponIk.update();
      } else if (!cleanerAnim || cleanerAnim.getProgress() <= 0.01) {
        hoseBones.forEach((bone, index) => {
          const t = index / Math.max(hoseBones.length - 1, 1);
          const phase = t * Math.PI;
          const amp = (0.035 + t * 0.2) * waveAmp;

          hoseWaveEuler.set(
            Math.sin(swayT * 0.32 + phase + 1.1) * amp * 0.04,
            Math.sin(swayT * 0.46 + phase) * amp,
            Math.sin(swayT * 0.31 + phase + 1.6) * amp * 0.16
          );
          hoseWaveQuaternion.setFromEuler(hoseWaveEuler);
          hoseTargetQuaternion
            .copy(hoseRestQuaternions[index])
            .multiply(hoseWaveQuaternion);
          bone.quaternion.slerp(hoseTargetQuaternion, 0.08);
        });
      } else {
        returnHoseQuaternionsToRest(
          getCleanerHoseReleaseRestBlend(animDeltaSeconds)
        );
      }

      const hasEffectorTarget = weaponIk.isReady || Boolean(fallbackTarget);

      if (weaponIk.isReady) {
        weaponIk.getEffectorWorldPosition(targetWorldPosition);
      } else {
        fallbackTarget?.getWorldPosition(targetWorldPosition);
      }

      if (hasEffectorTarget) {
        projectedEffectorCoords.copy(targetWorldPosition).project(camera);
      }

      objects.forEach((object) => {
        if (object.userData.hasHit) return;

        updateFloatingGarbageObject(
          object,
          roomSize,
          baseCameraHeight,
          scene,
          camera,
          {
            clampMaxYToCamera: true,
            deltaSeconds: garbageDeltaSeconds,
            maxCameraYOffset: garbageMaxCameraYOffset
          }
        );

        if (frameNow < nextSuctionAllowedAt) return;

        // Мусор живёт в gameGroup, а project(camera) ждёт world space.
        // После 3DoF-сдвига пола локальная object.position больше не совпадает
        // с реальной точкой на экране, поэтому проецируем world-позицию.
        object.getWorldPosition(objectWorldPosition);
        projectedCoords.copy(objectWorldPosition).project(camera);
        const distanceToEffector = projectedEffectorCoords.distanceTo(projectedCoords);
        projectedObject2d.set(projectedCoords.x, projectedCoords.y);
        const distanceToPointer = mousePos.distanceTo(projectedObject2d);
        const isTapTarget = tapSuctionTarget === object;

        if (
          canSuck &&
          hasEffectorTarget &&
          (distanceToEffector < SUCTION_HIT_RADIUS ||
            ((isTapTarget || activePointerId !== null) &&
              distanceToPointer < SUCTION_POINTER_RADIUS))
        ) {
          // BottomSheet с фактом должен ставить на паузу только игровые
          // действия. Камера и анимации продолжают жить в render loop.
          if (isGamePaused) return;
          if (object.userData.hasHit) return;

          const hit = object;
          object.userData.hasHit = true;
          if (tapSuctionTarget === object) {
            tapSuctionTarget = null;
            tapSuctionHoldUntil = 0;
          }
          nextSuctionAllowedAt = frameNow + SUCTION_NEXT_ITEM_DELAY_MS;
          navigator.vibrate?.(40);

          if (hit) {
            const root = object
            if (root) {
              const skipRemoval = false;
    
              if (!skipRemoval) {
                showGarbageCollectionFeedback(scene, root);

                if (hasEffectorTarget) {
                  targetLocalPosition.copy(targetWorldPosition);
                  // object.position локальная относительно gameGroup, поэтому
                  // world-точку носика пылесоса переводим в координаты родителя мусора.
                  object.parent?.worldToLocal(targetLocalPosition);
                  const startPos = object.position.clone();
                  const startScale = object.scale.clone();
                  const startRotation = object.rotation.clone();
                  const animatedTargetWorldPosition = targetWorldPosition.clone();
                  const animatedTargetLocalPosition = targetLocalPosition.clone();
                  object.traverse((child) => {
                    if (!(child instanceof Mesh)) return;

                    child.renderOrder = 1_000_002;
                    if (Array.isArray(child.material)) {
                      child.material = child.material.map((material) => {
                        const clonedMaterial = material.clone();
                        clonedMaterial.depthTest = false;
                        clonedMaterial.depthWrite = false;
                        return clonedMaterial;
                      });
                    } else {
                      child.material = child.material.clone();
                      child.material.depthTest = false;
                      child.material.depthWrite = false;
                    }
                  });
                  const tipBone = hoseBones[hoseBones.length - 1];
                  const canUsePipePath = hoseBones.length >= 2;
                  const pipeWorldPos = new Vector3();
                  const pipeToPos = new Vector3();
                  const pipeLocalPos = new Vector3();
                  const suctionDir = new Vector3();
                  const suctionMouthWorldPos = new Vector3();
                  const suctionRollWorldPos = new Vector3();
                  const suctionSideA = new Vector3();
                  const suctionSideB = new Vector3();
                  const suctionBasis = new Vector3();
                  const suctionPrevWorldPos = new Vector3();

                  const getSuctionTipTransform = (pos: Vector3, dir: Vector3) => {
                    if (tipBone) {
                      tipBone.getWorldPosition(pos);
                      const prevBone = hoseBones[hoseBones.length - 2];
                      if (prevBone) {
                        prevBone.getWorldPosition(suctionPrevWorldPos);
                        dir.subVectors(pos, suctionPrevWorldPos).normalize();
                      } else {
                        dir.set(0, -1, 0);
                      }
                    } else if (weaponIk.isReady) {
                      weaponIk.getEffectorWorldPosition(pos);
                      dir.set(0, -1, 0);
                    } else {
                      fallbackTarget?.getWorldPosition(pos);
                      dir.set(0, -1, 0);
                    }
                  };

                  showSuctionVortex(
                    scene,
                    getSuctionTipTransform,
                    SUCTION_ANIMATION_DURATION,
                    {
                      forwardOffset: 0.16,
                      renderOrder: 1_000_001,
                      scale: 1.46,
                      swirlStrength: 1.25,
                    },
                  );

                  const pipeGlow = createPipeTravelGlow(scene);
                  startWeaponUseSound();

                  animate({
                    duration: SUCTION_ANIMATION_DURATION,
                    onUpdate: (progress) => {
                      const p = MathUtils.clamp(progress, 0, 1);

                      if (p < SUCTION_ENTRY_PROGRESS || !canUsePipePath) {
                        pipeGlow.hide();
                        const entryProgress = MathUtils.clamp(
                          canUsePipePath ? p / SUCTION_ENTRY_PROGRESS : p,
                          0,
                          1
                        );

                        getSuctionTipTransform(animatedTargetWorldPosition, suctionDir);
                        if (suctionDir.lengthSq() <= 0.0001) {
                          suctionDir.set(0, -1, 0);
                        }
                        suctionDir.normalize();
                        suctionMouthWorldPos
                          .copy(animatedTargetWorldPosition)
                          .addScaledVector(suctionDir, SUCTION_FUNNEL_MOUTH_OFFSET);

                        if (entryProgress < SUCTION_FUNNEL_ROLL_START) {
                          const approachProgress = easeInOutCubic(
                            entryProgress / SUCTION_FUNNEL_ROLL_START
                          );

                          animatedTargetLocalPosition.copy(suctionMouthWorldPos);
                          object.parent?.worldToLocal(animatedTargetLocalPosition);
                          object.position.lerpVectors(
                            startPos,
                            animatedTargetLocalPosition,
                            approachProgress
                          );
                          object.rotation.copy(startRotation);
                          object.scale
                            .copy(startScale)
                            .multiplyScalar(MathUtils.lerp(1, 0.58, approachProgress));

                          return;
                        }

                        const rollProgress = MathUtils.clamp(
                          (entryProgress - SUCTION_FUNNEL_ROLL_START) /
                            (1 - SUCTION_FUNNEL_ROLL_START),
                          0,
                          1
                        );
                        const easedRollProgress = easeInOutCubic(rollProgress);
                        const rollAngle =
                          rollProgress * Math.PI * 2 * SUCTION_FUNNEL_ROLL_TURNS;
                        const rollRadius = MathUtils.lerp(
                          SUCTION_FUNNEL_ROLL_RADIUS,
                          0.006,
                          easedRollProgress
                        );

                        suctionBasis.set(
                          Math.abs(suctionDir.y) > 0.82 ? 1 : 0,
                          Math.abs(suctionDir.y) > 0.82 ? 0 : 1,
                          0
                        );
                        suctionSideA.crossVectors(suctionDir, suctionBasis).normalize();
                        suctionSideB.crossVectors(suctionDir, suctionSideA).normalize();

                        suctionRollWorldPos.lerpVectors(
                          suctionMouthWorldPos,
                          animatedTargetWorldPosition,
                          easedRollProgress
                        );
                        suctionRollWorldPos
                          .addScaledVector(suctionSideA, Math.cos(rollAngle) * rollRadius)
                          .addScaledVector(suctionSideB, Math.sin(rollAngle) * rollRadius);

                        animatedTargetLocalPosition.copy(suctionRollWorldPos);
                        object.parent?.worldToLocal(animatedTargetLocalPosition);
                        object.position.copy(animatedTargetLocalPosition);
                        object.rotation.set(
                          startRotation.x + easedRollProgress * Math.PI * 1.15,
                          startRotation.y + easedRollProgress * Math.PI * 2.1,
                          startRotation.z + easedRollProgress * Math.PI * 2.8
                        );
                        object.scale
                          .copy(startScale)
                          .multiplyScalar(
                            MathUtils.lerp(
                              0.58,
                              canUsePipePath ? SUCTION_ENTRY_SCALE : 0,
                              easedRollProgress
                            )
                          );
                      } else {
                        const pipeProgress =
                          (p - SUCTION_ENTRY_PROGRESS) /
                          (1 - SUCTION_ENTRY_PROGRESS);
                        const boneCount = hoseBones.length;
                        const scaledT = pipeProgress * (boneCount - 1);
                        const idx = Math.min(
                          Math.floor(scaledT),
                          boneCount - 2
                        );
                        const frac = scaledT - idx;
                        const fromBone = hoseBones[boneCount - 1 - idx];
                        const toBone = hoseBones[boneCount - 2 - idx];

                        fromBone.getWorldPosition(pipeWorldPos);
                        toBone.getWorldPosition(pipeToPos);
                        pipeWorldPos.lerp(pipeToPos, frac);

                        pipeLocalPos.copy(pipeWorldPos);
                        object.parent?.worldToLocal(pipeLocalPos);
                        object.position.copy(pipeLocalPos);
                        object.rotation.copy(startRotation);
                        object.scale
                          .copy(startScale)
                          .multiplyScalar(
                            MathUtils.lerp(
                              SUCTION_PIPE_ENTRY_SCALE,
                              SUCTION_EXIT_SCALE,
                              pipeProgress
                            )
                          );

                        pipeGlow.update(pipeWorldPos, pipeProgress);
                      }
                    },
                    onComplete: () => {
                      const {
                        setCollectedGarbageCount,
                        collectedGarbageCount,
                        totalGarbageCount
                      } = useSceneStore.getState();

                      setCollectedGarbageCount(collectedGarbageCount + 1);

                      if (collectedGarbageCount + 1 >= totalGarbageCount) {
                        onWin();
                      }

                      objects.delete(root);
                      if (cleanerDebrisTank) {
                        cleanerDebrisTank.addCollectedObject(root, startScale);
                      } else {
                        scene.remove(root);
                        disposeGarbageObject(root);
                      }
                      pipeGlow.dispose();
                    }
                  });
                }
              }
            }
          }
        }
      });
      garbageHintTracker.update(swayT);
    }
  };
};
